import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import {
  getSupportedContinents,
  getCountriesByContinent,
  getCitiesByCountryCode,
  getCountryName
} from '../services/locationService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const PageContainer = styled.main`
  padding: 100px 20px 60px;
  max-width: 1100px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 48px;
  margin-bottom: 15px;
  color: #1f1f1f;
`;

const IntroText = styled.p`
  font-size: 18px;
  color: #545454;
  line-height: 1.7;
  margin-bottom: 40px;
`;

const QuoteForm = styled.form`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  margin-bottom: 60px;
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 8px;
  color: #222;
  font-size: 14px;
`;

const Required = styled.span`
  color: #e53e3e;
  margin-left: 4px;
`;

const Input = styled.input`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(12, 77, 162, 0.12);
  }
`;

const Select = styled.select`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 16px;
  transition: border-color 0.2s ease;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(12, 77, 162, 0.12);
  }
`;

const Textarea = styled.textarea`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 16px;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(12, 77, 162, 0.12);
  }
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  grid-column: 1 / -1;
  margin-top: 10px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #333;
  cursor: pointer;
`;

const EstimatedPriceBox = styled.div`
  grid-column: 1 / -1;
  background: linear-gradient(135deg, #0c4da2, #4f94ff);
  color: white;
  padding: 24px;
  border-radius: 8px;
  text-align: center;
  margin-top: 10px;
`;

const PriceLabel = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const PriceValue = styled.div`
  font-size: 36px;
  font-weight: 700;
`;

const PriceNote = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 8px;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #0c4da2, #4f94ff);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  padding: 16px 32px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(12, 77, 162, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Disclaimer = styled.p`
  font-size: 14px;
  color: #777;
  margin: 0;
  text-align: center;
`;

const Alert = styled.div`
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  
  ${props => props.type === 'success' && `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}
  
  ${props => props.type === 'error' && `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
  
  ${props => props.type === 'info' && `
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  `}
`;

const SectionTitle = styled.h3`
  grid-column: 1 / -1;
  font-size: 20px;
  color: #0c4da2;
  margin: 20px 0 0 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #0c4da2;
`;

const getInitialFormData = () => ({
    name: '',
    company: '',
    phone: '',
    email: '',
    transport_type: '',
    origin: '',
    destination: '',
    quantity: '',
    unit: 'tonne',
    desired_date: '',
    is_round_trip: false,
    comments: '',
    originContinent: 'AF',
    originCountry: '',
    originCity: '',
    destinationContinent: 'AF',
    destinationCountry: '',
    destinationCity: ''
  });

const QuotePage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState(getInitialFormData);
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [transportTypes, setTransportTypes] = useState([]);

  useEffect(() => {
    // Fetch dynamic transport types
    const fetchTransportTypes = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/settings/public`);
        if (response.data.success) {
          setTransportTypes(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch transport types:', err);
        // Fallback defaults
        setTransportTypes(t('pages.quote.transportFallbacks'));
      }
    };
    fetchTransportTypes();
  }, [t]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Calculate estimated price when relevant fields change
  useEffect(() => {
    const calculatePrice = async () => {
      if (formData.origin && formData.destination) {
        setCalculatingPrice(true);
        try {
          // Calculate distance
          const distanceResponse = await axios.post(
            `${API_BASE_URL}/quotes/calculate-distance`,
            {
              origin: formData.origin,
              destination: formData.destination
            }
          );

          const distance_km = distanceResponse.data.data.distance_km;

          // Calculate price
          const priceResponse = await axios.post(
            `${API_BASE_URL}/quotes/calculate-price`,
            {
              distance_km,
              quantity: parseFloat(formData.quantity) || 0,
              is_round_trip: formData.is_round_trip,
              transport_type: formData.transport_type,
              origin_country: formData.originCountry,
              destination_country: formData.destinationCountry,
              origin_continent: formData.originContinent,
              destination_continent: formData.destinationContinent
            }
          );

          setEstimatedPrice(priceResponse.data.data.estimated_price);
        } catch (err) {
          console.error('Price calculation error:', err);
          setEstimatedPrice(null);
        } finally {
          setCalculatingPrice(false);
        }
      }
    };

    // Debounce the calculation
    const timer = setTimeout(calculatePrice, 1000);
    return () => clearTimeout(timer);
  }, [
    formData.origin,
    formData.destination,
    formData.quantity,
    formData.is_round_trip,
    formData.transport_type,
    formData.originCountry,
    formData.destinationCountry,
    formData.originContinent,
    formData.destinationContinent
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/quotes`, formData);

      if (response.data.success) {
        setSubmitted(true);
        
        // Reset form
        setFormData(getInitialFormData());
        setEstimatedPrice(null);

        // Hide success message after 8 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 8000);
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || t('pages.quote.errorFallback'));
    } finally {
      setLoading(false);
    }
  };

  const continents = useMemo(() => getSupportedContinents(language), [language]);
  const originCountries = useMemo(
    () => getCountriesByContinent(formData.originContinent, language),
    [formData.originContinent, language]
  );
  const destinationCountries = useMemo(
    () => getCountriesByContinent(formData.destinationContinent, language),
    [formData.destinationContinent, language]
  );
  const originCities = useMemo(
    () => getCitiesByCountryCode(formData.originCountry, language),
    [formData.originCountry, language]
  );
  const destinationCities = useMemo(
    () => getCitiesByCountryCode(formData.destinationCountry, language),
    [formData.destinationCountry, language]
  );

  const handleContinentChange = (type, value) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}Continent`]: value,
      [`${type}Country`]: '',
      [`${type}City`]: '',
      [type]: ''
    }));
  };

  const handleCountryChange = (type, value) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}Country`]: value,
      [`${type}City`]: '',
      [type]: ''
    }));
  };

  const handleCityChange = (type, value) => {
    setFormData((prev) => {
      const countryCode = prev[`${type}Country`];
      const countryLabel = getCountryName(countryCode);
      const formattedValue = value ? `${value}${countryLabel ? `, ${countryLabel}` : ''}` : '';

      return {
        ...prev,
        [`${type}City`]: value,
        [type]: formattedValue
      };
    });
  };

  return (
    <>
      <PageContainer>
        <PageTitle>{t('pages.quote.title')}</PageTitle>
        <IntroText>{t('pages.quote.intro')}</IntroText>

        {submitted && (
          <Alert type="success">
            {t('pages.quote.success')}
          </Alert>
        )}

        {error && (
          <Alert type="error">
            ❌ {error}
          </Alert>
        )}

        <QuoteForm onSubmit={handleSubmit}>
          <SectionTitle>{t('pages.quote.sections.contact')}</SectionTitle>
          
          <Field>
            <Label htmlFor="name">{t('pages.quote.fields.name')}<Required>*</Required></Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder={t('pages.quote.placeholders.name')}
              value={formData.name}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <Label htmlFor="company">{t('pages.quote.fields.company')}</Label>
            <Input
              id="company"
              name="company"
              type="text"
              placeholder={t('pages.quote.placeholders.company')}
              value={formData.company}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <Label htmlFor="phone">{t('pages.quote.fields.phone')}</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder={t('pages.quote.placeholders.phone')}
              value={formData.phone}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <Label htmlFor="email">{t('pages.quote.fields.email')}<Required>*</Required></Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t('pages.quote.placeholders.email')}
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Field>

          <SectionTitle>{t('pages.quote.sections.transport')}</SectionTitle>

          <Field style={{ gridColumn: '1 / -1' }}>
            <Label htmlFor="transport_type">{t('pages.quote.fields.transportType')}<Required>*</Required></Label>
            <Select
              id="transport_type"
              name="transport_type"
              value={formData.transport_type}
              onChange={handleChange}
              required
            >
              <option value="">{t('pages.quote.selects.selectType')}</option>
              {transportTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </Select>
          </Field>

          <SectionTitle>{t('pages.quote.sections.route')}</SectionTitle>

          <Field>
            <Label htmlFor="originContinent">{t('pages.quote.fields.originContinent')}<Required>*</Required></Label>
            <Select
              id="originContinent"
              name="originContinent"
              value={formData.originContinent}
              onChange={(event) => handleContinentChange('origin', event.target.value)}
              required
            >
              <option value="">{t('pages.quote.selects.selectContinent')}</option>
              {continents.map((continent) => (
                <option key={continent.code} value={continent.code}>
                  {continent.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="originCountry">{t('pages.quote.fields.originCountry')}<Required>*</Required></Label>
            <Select
              id="originCountry"
              name="originCountry"
              value={formData.originCountry}
              onChange={(event) => handleCountryChange('origin', event.target.value)}
              disabled={!formData.originContinent}
              required
            >
              <option value="">{t('pages.quote.selects.selectCountry')}</option>
              {originCountries.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="originCity">{t('pages.quote.fields.originCity')}<Required>*</Required></Label>
            <Select
              id="originCity"
              name="originCity"
              value={formData.originCity}
              onChange={(event) => handleCityChange('origin', event.target.value)}
              disabled={!formData.originCountry || originCities.length === 0}
              required
            >
              <option value="">{t('pages.quote.selects.selectCity')}</option>
              {originCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="destinationContinent">{t('pages.quote.fields.destinationContinent')}<Required>*</Required></Label>
            <Select
              id="destinationContinent"
              name="destinationContinent"
              value={formData.destinationContinent}
              onChange={(event) => handleContinentChange('destination', event.target.value)}
              required
            >
              <option value="">{t('pages.quote.selects.selectContinent')}</option>
              {continents.map((continent) => (
                <option key={continent.code} value={continent.code}>
                  {continent.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="destinationCountry">{t('pages.quote.fields.destinationCountry')}<Required>*</Required></Label>
            <Select
              id="destinationCountry"
              name="destinationCountry"
              value={formData.destinationCountry}
              onChange={(event) => handleCountryChange('destination', event.target.value)}
              disabled={!formData.destinationContinent}
              required
            >
              <option value="">{t('pages.quote.selects.selectCountry')}</option>
              {destinationCountries.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="destinationCity">{t('pages.quote.fields.destinationCity')}<Required>*</Required></Label>
            <Select
              id="destinationCity"
              name="destinationCity"
              value={formData.destinationCity}
              onChange={(event) => handleCityChange('destination', event.target.value)}
              disabled={!formData.destinationCountry || destinationCities.length === 0}
              required
            >
              <option value="">{t('pages.quote.selects.selectCity')}</option>
              {destinationCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="quantity">{t('pages.quote.fields.quantity')}</Label>
            <Input
              id="quantity"
              name="quantity"
              type="number"
              step="0.01"
              min="0"
              placeholder={t('pages.quote.placeholders.quantity')}
              value={formData.quantity}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <Label htmlFor="unit">{t('pages.quote.fields.unit')}</Label>
            <Select
              id="unit"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
            >
              <option value="tonne">{t('pages.quote.units.tonne')}</option>
              <option value="m3">{t('pages.quote.units.m3')}</option>
            </Select>
          </Field>

          <Field>
            <Label htmlFor="desired_date">{t('pages.quote.fields.desiredDate')}</Label>
            <Input
              id="desired_date"
              name="desired_date"
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={formData.desired_date}
              onChange={handleChange}
            />
          </Field>

          <CheckboxField>
            <Checkbox
              id="is_round_trip"
              name="is_round_trip"
              type="checkbox"
              checked={formData.is_round_trip}
              onChange={handleChange}
            />
            <CheckboxLabel htmlFor="is_round_trip">
              {t('pages.quote.fields.roundTrip')}
            </CheckboxLabel>
          </CheckboxField>

          <Field style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
            <Label htmlFor="comments">{t('pages.quote.fields.comments')}</Label>
            <Textarea
              id="comments"
              name="comments"
              placeholder={t('pages.quote.placeholders.comments')}
              value={formData.comments}
              onChange={handleChange}
            />
          </Field>

          {estimatedPrice !== null && !calculatingPrice && (
            <EstimatedPriceBox>
              <PriceLabel>{t('pages.quote.estimate.label')}</PriceLabel>
              <PriceValue>{estimatedPrice.toFixed(2)} MAD</PriceValue>
              <PriceNote>
                {t('pages.quote.estimate.note')}
              </PriceNote>
            </EstimatedPriceBox>
          )}

          {calculatingPrice && (
            <Alert type="info" style={{ gridColumn: '1 / -1' }}>
              {t('pages.quote.calculating')}
            </Alert>
          )}

          <Actions>
            <SubmitButton type="submit" disabled={loading || calculatingPrice}>
              {loading ? t('pages.quote.submit.loading') : t('pages.quote.submit.idle')}
            </SubmitButton>
            <Disclaimer>
              {t('pages.quote.disclaimer')}
            </Disclaimer>
          </Actions>
        </QuoteForm>
      </PageContainer>
      <CTASection />
      <Footer />
    </>
  );
};

export default QuotePage;
